import { useEffect, useMemo, useState } from "react";
import { AiFillWarning } from "react-icons/ai";
import { BiCaretDown, BiCaretRight } from "react-icons/bi";
import { FaMapMarkerAlt } from "react-icons/fa";

const fieldHasMarker = (field, markersByFieldId) => Boolean(markersByFieldId[field.id]);

const fieldHasUnmetMarkerRequirement = (field, markersByFieldId) =>
	Boolean(field.require_aerial_view_marker) && !fieldHasMarker(field, markersByFieldId);

const fieldIsPubliclyEditable = (field) => Boolean(field.public_editable);

const fieldMatchesCatalogueFilters = (
	field,
	markersByFieldId,
	showOnlyFieldsWithMarkers,
	showOnlyUnmetMarkerRequirements,
	showOnlyEditableFields,
) => {
	if (showOnlyEditableFields) {
		return fieldIsPubliclyEditable(field);
	}
	const hasMarker = fieldHasMarker(field, markersByFieldId);
	const hasUnmetMarkerRequirement = fieldHasUnmetMarkerRequirement(field, markersByFieldId);
	if (showOnlyFieldsWithMarkers && showOnlyUnmetMarkerRequirements) {
		return hasMarker || hasUnmetMarkerRequirement;
	}
	if (showOnlyFieldsWithMarkers) {
		return hasMarker;
	}
	if (showOnlyUnmetMarkerRequirements) {
		return hasUnmetMarkerRequirement;
	}
	return true;
};

const catalogueEmptyMessage = (showOnlyFieldsWithMarkers, showOnlyUnmetMarkerRequirements, showOnlyEditableFields) => {
	if (showOnlyEditableFields) {
		return "No editable fields.";
	}
	if (showOnlyFieldsWithMarkers && showOnlyUnmetMarkerRequirements) {
		return "No fields match these filters.";
	}
	if (showOnlyUnmetMarkerRequirements) {
		return "No fields are missing a required marker.";
	}
	if (showOnlyFieldsWithMarkers) {
		return "No fields have markers yet.";
	}
	return "No fields available.";
};

const CatalogueFilterSwitch = ({ label, checked, disabled = false, onToggle }) => (
	<div className="flex items-center justify-between gap-2 px-3 py-2">
		<span className={`text-sm font-semibold ${disabled ? "text-gray-400" : ""}`}>{label}</span>
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={label}
			disabled={disabled}
			className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
				disabled ? "cursor-not-allowed opacity-50" : ""
			} ${checked ? "bg-blue-600" : "bg-gray-300"}`}
			onClick={onToggle}>
			<span
				className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
					checked ? "translate-x-5" : "translate-x-0"
				}`}
			/>
		</button>
	</div>
);

const AerialCatalogue = ({
	sections,
	markersByFieldId,
	selectedFieldId,
	readOnly,
	isGuest = false,
	onSelectField,
	onBeginPlacement,
	onOpenDetail,
}) => {
	const [openSectionIds, setOpenSectionIds] = useState([]);
	const [showOnlyFieldsWithMarkers, setShowOnlyFieldsWithMarkers] = useState(false);
	const [showOnlyUnmetMarkerRequirements, setShowOnlyUnmetMarkerRequirements] = useState(false);
	const [showOnlyEditableFields, setShowOnlyEditableFields] = useState(false);

	const visibleSections = useMemo(() => {
		return sections
			.map((section) => ({
				...section,
				fields: section.fields.filter((field) =>
					fieldMatchesCatalogueFilters(
						field,
						markersByFieldId,
						showOnlyFieldsWithMarkers,
						showOnlyUnmetMarkerRequirements,
						showOnlyEditableFields,
					),
				),
			}))
			.filter((section) => section.fields.length > 0);
	}, [markersByFieldId, sections, showOnlyEditableFields, showOnlyFieldsWithMarkers, showOnlyUnmetMarkerRequirements]);

	useEffect(() => {
		if (!selectedFieldId) {
			return;
		}
		const selectedSection = visibleSections.find((section) => section.fields.some((field) => field.id === selectedFieldId));
		if (selectedSection) {
			setOpenSectionIds((currentIds) => (currentIds.includes(selectedSection.id) ? currentIds : [...currentIds, selectedSection.id]));
		}
	}, [selectedFieldId, visibleSections]);

	const toggleSection = (sectionId) => {
		setOpenSectionIds((currentIds) =>
			currentIds.includes(sectionId) ? currentIds.filter((currentId) => currentId !== sectionId) : [...currentIds, sectionId],
		);
	};

	const toggleEditableOnly = () => {
		if (showOnlyEditableFields) {
			setShowOnlyEditableFields(false);
			return;
		}
		setShowOnlyFieldsWithMarkers(false);
		setShowOnlyUnmetMarkerRequirements(false);
		setShowOnlyEditableFields(true);
	};

	return (
		<div>
			<div className="border-b border-gray-200">
				<CatalogueFilterSwitch
					label="Has Markers"
					checked={showOnlyFieldsWithMarkers}
					disabled={showOnlyEditableFields}
					onToggle={() => setShowOnlyFieldsWithMarkers((currentValue) => !currentValue)}
				/>
				<div className="border-t border-gray-200">
					<CatalogueFilterSwitch
						label="Marker Requirement Unmet"
						checked={showOnlyUnmetMarkerRequirements}
						disabled={showOnlyEditableFields}
						onToggle={() => setShowOnlyUnmetMarkerRequirements((currentValue) => !currentValue)}
					/>
				</div>
				{isGuest ? (
					<div className="border-t-4 border-gray-200">
						<CatalogueFilterSwitch label="Editable Only" checked={showOnlyEditableFields} onToggle={toggleEditableOnly} />
					</div>
				) : null}
			</div>
			<div className="max-h-[65vh] overflow-y-auto">
				{visibleSections.length > 0 ? (
					visibleSections.map((section) => {
						const isOpen = openSectionIds.includes(section.id);
						return (
							<div key={section.id} className="border-b border-gray-200">
								<button
									type="button"
									className="flex w-full items-center gap-2 bg-gray-100 px-3 py-2 text-left font-semibold hover:bg-gray-200"
									onClick={() => toggleSection(section.id)}>
									{isOpen ? <BiCaretDown /> : <BiCaretRight />}
									<span className="flex-1">{section.name}</span>
									<span className="text-xs text-gray-500">{section.fields.length}</span>
								</button>
								{isOpen ? (
									<div>
										{section.fields.map((field) => {
											const isSelected = selectedFieldId === field.id;
											const hasMarker = fieldHasMarker(field, markersByFieldId);
											const hasUnmetMarkerRequirement = fieldHasUnmetMarkerRequirement(field, markersByFieldId);
											return (
												<div
													key={field.id}
													className={`flex w-full items-center gap-2 border-l-4 py-1 pl-5 pr-2 text-sm ${
														isSelected ? "border-blue-600 bg-blue-100" : "border-gray-300 hover:bg-blue-50"
													}`}>
													<button
														type="button"
														className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
														onClick={() => onSelectField(field.id)}>
														{hasUnmetMarkerRequirement ? (
															<AiFillWarning
																className="text-amber-500"
																role="img"
																aria-label="Marker requirement unmet"
															/>
														) : (
															<FaMapMarkerAlt
																className={
																	isSelected ? "text-red-600" : hasMarker ? "text-blue-600" : "text-gray-300"
																}
															/>
														)}
														<span className="min-w-0 flex-1 truncate">{field.name}</span>
													</button>
													{isSelected ? (
														hasMarker ? (
															<button
																type="button"
																className="shrink-0 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
																onClick={onOpenDetail}>
																Detail
															</button>
														) : readOnly ? null : (
															<button
																type="button"
																disabled={isGuest && !field.public_editable}
																className="shrink-0 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
																onClick={() => onBeginPlacement(field.id)}>
																Create Marker
															</button>
														)
													) : null}
												</div>
											);
										})}
									</div>
								) : null}
							</div>
						);
					})
				) : (
					<p className="px-3 py-4 text-sm text-gray-500">
						{catalogueEmptyMessage(showOnlyFieldsWithMarkers, showOnlyUnmetMarkerRequirements, showOnlyEditableFields)}
					</p>
				)}
			</div>
		</div>
	);
};

export default AerialCatalogue;
